import dayjs from 'dayjs';
import { HTMLProps, useLayoutEffect, useState } from 'react';
import { useToggle } from '../../../hooks/use-toggle';

export interface DatePickerProps extends HTMLProps<HTMLDivElement> {
  selectedDate?: Date;
  onSelectDate: (date?: Date) => void;
  minDate?: Date;
  maxDate?: Date;
}

export const useDatePicker = (props: DatePickerProps) => {
  const { selectedDate, onSelectDate } = props;
  const [inputValue, setInputValue] = useState('');
  const [currentDate, setCurrentDate] = useState(selectedDate || new Date());

  const [open, toggleOpen, setOpen] = useToggle();

  /** Сравнение по timestamp: родитель часто передаёт `new Date(sec)` каждый рендер — иначе сбрасывается ввод и календарь. */
  const selectedAtMs = selectedDate != null ? selectedDate.getTime() : null;

  useLayoutEffect(() => {
    if (selectedAtMs == null) {
      setCurrentDate(new Date());
      setInputValue('');
      return;
    }
    const d = new Date(selectedAtMs);
    setCurrentDate(d);
    setInputValue(d.toLocaleDateString('ru-RU'));
  }, [selectedAtMs]);

  const handleSelectDate = (date: Date) => {
    onSelectDate(date);
    setInputValue(date.toLocaleDateString('ru-RU')); // Форматируем дату как DD.MM.YYYY
    toggleOpen();
  };

  const handleInputChange = (e: string) => {
    let value = e.replace(/\D/g, ''); // Удаляем все нечисловые символы
    if (value.length > 8) value = value.slice(0, 8); // Ограничиваем длину до 8 символов

    if (value.length >= 5) {
      value = `${value.slice(0, 2)}.${value.slice(2, 4)}.${value.slice(4)}`; // Добавляем слеши между днями, месяцами и годами
    } else if (value.length >= 3) {
      value = `${value.slice(0, 2)}.${value.slice(2)}`; // Добавляем слеш между днями и месяцами
    }
    setInputValue(value);
    if (value.length === 10) {
      const date = dayjs(value, 'DD.MM.YYYY').toDate();
      if (dayjs(date).isValid()) {
        onSelectDate(date);
      }
    }
    if (value.length === 0) {
      onSelectDate(undefined);
    }
  };

  const handleResetDate = () => {
    setInputValue('');
    onSelectDate(undefined);
  };

  const handleClose = () => setOpen(false);

  return {
    currentDate,
    inputValue,
    handleSelectDate,
    handleInputChange,
    open,
    handleResetDate,
    handleClose,
    setOpen,
  };
};
